import { useEffect, useState } from 'react';
import {
  assertNever,
  type TriageActionNeeded,
  type TriageItem,
} from '@signalvote/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { KeyboardHintBar, type KeyboardShortcut } from '../KeyboardHintBar';
import {
  useFinanceKeyboardShortcuts,
  type TriagePickerTile,
} from '../useFinanceKeyboardShortcuts';
import {
  BankLineBody,
  ClassifyOrganizationBody,
  ContributorShapeBody,
  ExpenditureBody,
  MissingInfoBody,
  OrphanRefundBody,
  PendingContributionBody,
} from './triage';

interface TriageModalProps {
  items: TriageItem[];
  open: boolean;
  onClose: () => void;
}

/**
 * USI-012: per-kind triage shortcut sets. The hint bar advertises only
 * the keys that are live for the current item's kind so the keys the
 * treasurer sees always match the keys the active body actually
 * dispatches. The keymap-by-kind contract is mirrored in CONVENTIONS.md
 * § Keyboard shortcuts; both must be edited together.
 *
 * Modal-level keys (Esc, Skip) appear in every kind. Body-level keys
 * (D/E/X picker, Enter confirm/save/pick, R request donor info, P
 * pick parent, S skip-to-standalone override) appear only in the kinds
 * whose body registers a handler for them.
 *
 * The empty / unknown fallback shows just Esc — there is no body to
 * dispatch body-level keys into.
 */
const BANK_LINE_SHORTCUTS: ReadonlyArray<KeyboardShortcut> = [
  { keys: 'D · E · X', label: 'Pick tile' },
  { keys: 'Esc', label: 'Close' },
  { keys: 'S', label: 'Skip' },
];

const PENDING_CONTRIBUTION_SHORTCUTS: ReadonlyArray<KeyboardShortcut> = [
  { keys: 'Enter', label: 'Confirm' },
  { keys: 'Esc', label: 'Close' },
  { keys: 'S', label: 'Skip' },
];

const MISSING_INFO_SHORTCUTS: ReadonlyArray<KeyboardShortcut> = [
  { keys: 'Enter', label: 'Save' },
  // USI-010: R fires "Mark as information requested" on the
  // missing-info-contribution body only.
  { keys: 'R', label: 'Request donor info' },
  { keys: 'Esc', label: 'Close' },
  { keys: 'S', label: 'Skip' },
];

const ORPHAN_REFUND_SHORTCUTS: ReadonlyArray<KeyboardShortcut> = [
  { keys: 'Enter', label: 'Pick highlighted' },
  // USI-011: P fires "Pick parent" on the orphan-refund body; while
  // mounted, S overrides the modal-level Skip to mean "Skip to
  // standalone" (a state-changing audit-emit action, not navigation) per
  // CONVENTIONS.md § Keyboard shortcuts. The label reads "Skip /
  // Standalone" so the treasurer knows what S does on this kind.
  { keys: 'P', label: 'Pick parent' },
  { keys: 'Esc', label: 'Close' },
  { keys: 'S', label: 'Skip / Standalone' },
];

// ULL-003: a ProvideExpenditureInfo expenditure — Enter saves the payee
// address (once complete); the Confirm-as-draft button is click-only.
const EXPENDITURE_INFO_SHORTCUTS: ReadonlyArray<KeyboardShortcut> = [
  { keys: 'Enter', label: 'Save address' },
  { keys: 'Esc', label: 'Close' },
  { keys: 'S', label: 'Skip' },
];

// Issue #322 — the contributor-shape editor: Enter saves the corrected donor once the name
// is complete.
const CONTRIBUTOR_SHAPE_SHORTCUTS: ReadonlyArray<KeyboardShortcut> = [
  { keys: 'Enter', label: 'Save donor' },
  { keys: 'Esc', label: 'Close' },
  { keys: 'S', label: 'Skip' },
];

const EMPTY_SHORTCUTS: ReadonlyArray<KeyboardShortcut> = [
  { keys: 'Esc', label: 'Close' },
];

/**
 * ATL-001: the hint set keys off `actionNeeded`, not `kind` — the modal queue
 * is all `NeedsAction`, and the triage reason (which drives the keymap) now
 * rides on `actionNeeded` rather than the kind. A Contribution can be either
 * Confirm (Enter) or ProvideDonorInfo (R), so dispatching on kind would advertise
 * the wrong keys.
 */
function shortcutsForAction(
  actionNeeded: TriageActionNeeded | null,
): ReadonlyArray<KeyboardShortcut> {
  if (actionNeeded === null) return EMPTY_SHORTCUTS;
  switch (actionNeeded) {
    case 'Classify':
      return BANK_LINE_SHORTCUTS;
    case 'Confirm':
      return PENDING_CONTRIBUTION_SHORTCUTS;
    case 'ProvideDonorInfo':
      return MISSING_INFO_SHORTCUTS;
    case 'MatchRefund':
      return ORPHAN_REFUND_SHORTCUTS;
    case 'ProvideExpenditureInfo':
      // ULL-003 — the expenditure "add payee info" body registers Enter→Save
      // (once the address is complete) and shows a Confirm-as-draft button.
      return EXPENDITURE_INFO_SHORTCUTS;
    case 'CategorizeExpenditure':
      // The fix is the inline Category dropdown already in the expanded
      // expenditure body — no dedicated keymap, so just Esc→Close.
      return EMPTY_SHORTCUTS;
    case 'ResolveContributorShape':
      // Issue #322 — the contributor-shape editor registers Enter→Save donor.
      return CONTRIBUTOR_SHAPE_SHORTCUTS;
    case 'ClassifyOrganization':
      // The fix is the inline organization-type dropdown in the expanded body,
      // which registers Enter→Save once a subtype is picked — no dedicated keymap.
      return EMPTY_SHORTCUTS;
    default:
      return assertNever(actionNeeded);
  }
}

/**
 * USI-007 (ADR-011): unified Sort Triage modal shell. Per design doc §
 * Frontend additions, this is the modal that owns the session queue,
 * branches per `kind` into the per-kind body components, and handles the
 * keyboard layer + chronological-resume contract.
 *
 * The shell renders an empty placeholder body per kind in this ticket;
 * USI-008 / USI-009 / USI-010 / USI-011 fill in the bodies. Splitting
 * shell from per-kind body lets each `kind` ship independently.
 *
 * Skip is navigation-only per ADR-011 commitment #12 — no `TriageSkipNote`
 * persistence; the session-scoped queue forgets skipped items the moment
 * the modal closes.
 *
 * Chronological resume: when a per-kind body classifies a row (USI-008+
 * wire `onClassify`), the modal invalidates the sortInbox query so the
 * new Pending row created server-side surfaces in the next refresh, and
 * advances `selectedIndex` so the treasurer never re-sees what they just
 * classified.
 */
export function TriageModal({ items, open, onClose }: TriageModalProps) {
  // ATL-001: the modal is the focused triage WORK queue — filter to
  // `NeedsAction` so the all-time ledger's Done / Reported rows (which ride in
  // the same `items` array the InboxStrip passes through) never render a triage
  // body (e.g. a Confirm button for an already-Confirmed contribution) and
  // never inflate the "X of N" progress count. The ledger view (TriageLedger)
  // is where the all-time rows live.
  const queue = items.filter((i) => i.status === 'NeedsAction');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hintVisible, setHintVisible] = useState(true);
  // USI-008: bank-line tile picker state. Lifted here so the modal's
  // keyboard shortcut hook (D/E/X) and the body's click handlers share
  // the same source of truth. Resets whenever the visible item changes
  // (advance / refetch / re-anchor).
  const [pickedTile, setPickedTile] = useState<TriagePickerTile | null>(null);
  // USI-009: registration slot for the active per-kind body's Enter-key
  // handler. PendingContributionBody (and future Enter-driven bodies)
  // registers its confirm action here so the modal's triage-scope
  // keyboard hook can route Enter to the right side-effect per kind
  // instead of falling back to `handleClassify` (which would advance the
  // queue without firing the body's mutation). Object-wrapped so React's
  // useState doesn't interpret a bare function as a computed updater.
  const [bodyConfirm, setBodyConfirm] = useState<{ fn: () => void } | null>(
    null,
  );
  // USI-010: registration slot for the missing-info body's R-key
  // attestation handler. Same shape as bodyConfirm; resets per item so a
  // stale registration can't fire on the next row.
  const [bodyRequestDonorInfo, setBodyRequestDonorInfo] = useState<{
    fn: () => void;
  } | null>(null);
  // USI-011: registration slot for the orphan-refund body's P-key
  // "Pick parent" handler.
  const [bodyPickParent, setBodyPickParent] = useState<{
    fn: () => void;
  } | null>(null);
  // USI-011: registration slot for the orphan-refund body's S-key
  // override. When registered, S routes to "Skip to standalone" (a
  // state-changing audit-emit action); when null, S falls back to the
  // modal-level navigation skip (advance to next item). Per CONVENTIONS.md
  // § Keyboard shortcuts.
  const [bodyStandalone, setBodyStandalone] = useState<{
    fn: () => void;
  } | null>(null);

  // Reset selectedIndex when the queue's identity changes or the modal
  // opens. The local index is session-scoped per ADR-011 commitment #12.
  // Keying off the concatenated row ids (not `items.length`) means a
  // mid-modal refetch that produces the same length but different rows
  // — common after a classify invalidates the inbox — still re-anchors
  // to the top.
  const itemsKey = queue.map((i) => i.id).join('|');
  useEffect(() => {
    if (open) setSelectedIndex(0);
  }, [open, itemsKey]);

  const current = queue[selectedIndex];

  useEffect(() => {
    setPickedTile(null);
    setBodyConfirm(null);
    setBodyRequestDonorInfo(null);
    setBodyPickParent(null);
    setBodyStandalone(null);
  }, [current?.id]);

  const advance = () => {
    if (selectedIndex + 1 >= queue.length) {
      onClose();
    } else {
      setSelectedIndex((i) => i + 1);
    }
  };

  // USI-012: single source of truth for "what S means right now." Both
  // the S-key (in the triage-scope keyboard hook) and the Skip BUTTON
  // route through this so the keyboard and click paths can't drift.
  // Pre-USI-012 the Skip button bypassed `bodyStandalone`, so clicking
  // Skip on an orphan-refund silently advanced the queue instead of
  // firing skip-to-standalone — defeating the audit-defensibility
  // narrative ADR-011 § Negative consequences names load-bearing.
  const handleSkip = () => {
    if (bodyStandalone) {
      bodyStandalone.fn();
    } else {
      advance();
    }
  };

  // Per-kind callback the per-kind bodies (USI-008+) call after a
  // successful classify. Default behavior:
  // The classify's write set refreshes the inbox; this advances to the next item (or closes
  // if at end).
  const handleClassify = () => {
    advance();
  };

  useFinanceKeyboardShortcuts({
    onClose,
    // USI-011: S key. When the orphan-refund body is mounted it registers
    // a standalone override (a state-changing audit-emit action); other
    // kinds leave the slot null and S falls back to the modal-level
    // navigation skip (advance to next item). USI-012 unified this with
    // the Skip button via `handleSkip` so the keyboard and click paths
    // share one source of truth.
    onTriageSkip: handleSkip,
    // USI-009: per-kind dispatch on Enter. A registered body handler
    // (today: PendingContributionBody's Confirm, MissingInfoBody's Save,
    // OrphanRefundBody's Pick highlighted) takes precedence. USI-012:
    // when no body registered, Enter is a SILENT NO-OP — the previous
    // fallback to `handleClassify` would invalidate-and-advance on
    // bank-line / orphan-refund-with-no-highlight, which silently moved
    // the queue without classifying. That contradicts the AC's per-kind
    // hint isolation contract (the bar omits Enter on those kinds) and
    // the explicit Skip-button design. Bank-line classify happens
    // through the embedded classification panel's own form submit, not
    // a modal-level Enter fallback.
    onTriageConfirm: () => {
      if (bodyConfirm) {
        bodyConfirm.fn();
      }
    },
    onTriagePick: (tile) => {
      // USI-008: D/E/X keyboard picks only apply to the bank-line body.
      // Other kinds use their own keymaps (R for missing-info, P/S for
      // orphan-refund, Enter for pending-contribution); the shell ignores
      // mis-pressed picker keys on those kinds.
      if (current?.kind === 'BankLine') {
        setPickedTile(tile);
      }
    },
    // USI-010: R fires the missing-info body's attestation. The body
    // registers a handler when mounted; other kinds (bank-line,
    // pending-contribution, orphan-refund) never register, so R is a
    // silent no-op on those kinds. Per CONVENTIONS.md § Keyboard
    // shortcuts.
    onTriageRequestDonorInfo: () => {
      if (bodyRequestDonorInfo) {
        bodyRequestDonorInfo.fn();
      }
    },
    // USI-011: P fires "Pick parent" on the orphan-refund body. Other
    // kinds don't register, so P is a silent no-op on those kinds.
    onTriagePickParent: () => {
      if (bodyPickParent) {
        bodyPickParent.fn();
      }
    },
    scope: 'triage',
  });

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent
          data-testid="triage-modal"
          className="max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle>
              {queue.length === 0
                ? 'Inbox clear'
                : `Triage — ${selectedIndex + 1} of ${queue.length}`}
            </DialogTitle>
            {/* USI-015: wires Radix aria-describedby + elderly-user a11y
                (user_canvasser_demographics.md); clears the Radix warning. */}
            <DialogDescription className="sr-only">
              Review each item that arrived and isn't on a report yet, then
              classify, confirm, or skip it.
            </DialogDescription>
          </DialogHeader>

          {queue.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No items to triage. Close to return.
            </p>
          ) : current ? (
            <div className="flex flex-col gap-4">
              <PerKindBody
                item={current}
                onClassify={handleClassify}
                onClose={onClose}
                pickedTile={pickedTile}
                onPickTile={setPickedTile}
                onRegisterConfirm={setBodyConfirm}
                onRegisterRequestDonorInfo={setBodyRequestDonorInfo}
                onRegisterPickParent={setBodyPickParent}
                onRegisterStandalone={setBodyStandalone}
              />
              <div className="flex items-center justify-between border-t pt-3">
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="triage-skip"
                  onClick={handleSkip}
                >
                  Skip
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="triage-close"
                  onClick={onClose}
                >
                  Close
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {open && (
        <KeyboardHintBar
          visible={hintVisible}
          onToggle={setHintVisible}
          shortcuts={shortcutsForAction(current?.actionNeeded ?? null)}
        />
      )}
    </>
  );
}

// ============================================================================
// Per-kind dispatch. USI-008 landed `bank-line`; USI-009 landed
// `pending-contribution`; USI-010 / USI-011 replace the two remaining
// placeholders in turn.
// ============================================================================

interface PerKindBodyProps {
  item: TriageItem;
  onClassify: () => void;
  onClose: () => void;
  pickedTile: TriagePickerTile | null;
  onPickTile: (tile: TriagePickerTile) => void;
  onRegisterConfirm: (handler: { fn: () => void } | null) => void;
  onRegisterRequestDonorInfo: (handler: { fn: () => void } | null) => void;
  onRegisterPickParent: (handler: { fn: () => void } | null) => void;
  onRegisterStandalone: (handler: { fn: () => void } | null) => void;
}

function PerKindBody({
  item,
  onClassify,
  onClose,
  pickedTile,
  onPickTile,
  onRegisterConfirm,
  onRegisterRequestDonorInfo,
  onRegisterPickParent,
  onRegisterStandalone,
}: PerKindBodyProps) {
  switch (item.kind) {
    case 'BankLine':
      return (
        <BankLineBody
          item={item}
          pickedTile={pickedTile}
          onPickTile={onPickTile}
          onClassified={onClassify}
          onClose={onClose}
        />
      );
    case 'Contribution':
      // ATL-001: the kind no longer encodes the triage reason — branch on
      // `actionNeeded` to pick the right body. ProvideDonorInfo → the fill
      // panel; Confirm (and any other Contribution NeedsAction) → the
      // confirm/void body.
      if (item.actionNeeded === 'ResolveContributorShape') {
        // Issue #322 — the inline contributor-shape editor (person-vs-org fix).
        return (
          <ContributorShapeBody
            item={item}
            contactConcurrencyVersion={item.contribution.contactConcurrencyVersion}
            onClassified={onClassify}
            onRegisterConfirm={onRegisterConfirm}
          />
        );
      }
      if (item.actionNeeded === 'ClassifyOrganization') {
        // ADR-057 — the inline organization-subtype picker (AL cbContributionSource).
        return (
          <ClassifyOrganizationBody
            item={item}
            contactConcurrencyVersion={item.contribution.contactConcurrencyVersion}
            onClassified={onClassify}
            onRegisterConfirm={onRegisterConfirm}
          />
        );
      }
      return item.actionNeeded === 'ProvideDonorInfo' ? (
        <MissingInfoBody
          item={item}
          // AFC-002 — thread the REAL donor-contact version (now required on the
          // wire, populated by ManualEntryService.MapContributionToDto). The old
          // hardcoded `0` was the round-1 concurrency bug
          // (project_triage_addinfo_concurrency_bug): it 409'd the first Save for
          // any donor contact previously touched, silently blocking the close.
          contactConcurrencyVersion={item.contribution.contactConcurrencyVersion}
          onClassified={onClassify}
          onRegisterConfirm={onRegisterConfirm}
          onRegisterRequestDonorInfo={onRegisterRequestDonorInfo}
        />
      ) : (
        <PendingContributionBody
          item={item}
          onClassified={onClassify}
          onRegisterConfirm={onRegisterConfirm}
        />
      );
    case 'Expenditure':
      // ULL-003: a born-Pending expenditure (bank-classify / receipt-capture)
      // now reaches the NeedsAction queue. Confirm finalizes it;
      // ProvideExpenditureInfo adds the payee-address editor before confirming.
      return (
        <ExpenditureBody
          item={item}
          onClassified={onClassify}
          onRegisterConfirm={onRegisterConfirm}
        />
      );
    case 'Refund':
      return (
        <OrphanRefundBody
          item={item}
          onClassified={onClassify}
          onRegisterConfirm={onRegisterConfirm}
          onRegisterPickParent={onRegisterPickParent}
          onRegisterStandalone={onRegisterStandalone}
        />
      );
    default:
      // Compile-time exhaustiveness via the codebase-wide `assertNever`
      // helper (root CLAUDE.md § Code Quality). A new TriageItemKind that
      // forgets to land a body throws here instead of silently rendering
      // nothing.
      return assertNever(item);
  }
}
